import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { PastChallengeCard } from './PastChallengeCard';
import { Tables } from '@/integrations/supabase/types';
import { Skeleton } from '@/components/ui/skeleton';

type Group = Tables<'groups'>;
type Membership = Tables<'memberships'>;

interface PastChallengeData {
  group: Group;
  userStats: {
    totalDrinks: number;
    longestStreak: number;
    beerCount: number;
    wineCount: number;
    shotCount: number;
  };
  leaderboard: {
    user_id: string;
    nickname: string;
    total: number;
    longestStreak: number;
    rank: number;
  }[];
}

export function PastChallenges() {
  const { user, profile } = useAuth();
  const { t } = useLanguage();

  const { data: pastChallenges, isLoading } = useQuery({
    queryKey: ['past-challenges', user?.id],
    queryFn: async (): Promise<PastChallengeData[]> => {
      if (!user) return [];

      // Fetch all memberships for the user
      const { data: memberships } = await supabase
        .from('memberships')
        .select('*, groups(*)')
        .eq('user_id', user.id);

      if (!memberships || memberships.length === 0) return [];

      const now = new Date();
      const results: PastChallengeData[] = [];

      for (const membership of memberships) {
        const group = membership.groups as unknown as Group;
        if (!group) continue;

        // Check if this challenge is archived
        const challengeEnd = new Date(group.challenge_end);
        const isArchived = group.status === 'completed' || challengeEnd <= now;

        if (!isArchived) continue;

        // Fetch drink logs for this group
        const { data: allLogs } = await supabase
          .from('drink_logs')
          .select('*')
          .eq('group_id', group.id);

        // Calculate user's stats
        const userLogs = (allLogs || []).filter(log => log.user_id === user.id);
        const totalDrinks = userLogs.length;
        const beerCount = userLogs.filter(d => d.drink_type === 'beer').length;
        const wineCount = userLogs.filter(d => d.drink_type === 'wine').length;
        const shotCount = userLogs.filter(d => d.drink_type === 'shot').length;

        // Calculate longest streak for user
        const longestStreak = calculateLongestStreak(userLogs, group);

        // Fetch all members of this group for leaderboard
        const { data: groupMemberships } = await supabase
          .from('memberships')
          .select('user_id')
          .eq('group_id', group.id);

        // Get profiles for all members using the secure RPC function
        const memberIds = groupMemberships?.map(m => m.user_id) || [];
        const { data: profiles } = await supabase
          .rpc('get_group_member_profiles', { target_group_id: group.id });

        // Build leaderboard
        const leaderboardData = memberIds.map(memberId => {
          const memberProfile = profiles?.find(p => p.id === memberId);
          const memberLogs = (allLogs || []).filter(log => log.user_id === memberId);
          const memberTotal = memberLogs.length;
          const memberStreak = calculateLongestStreak(memberLogs, group);

          return {
            user_id: memberId,
            nickname: memberProfile?.user_id || 'Unknown',
            total: memberTotal,
            longestStreak: memberStreak,
            rank: 0,
          };
        });

        // Sort: by total ascending, then by longest streak descending
        leaderboardData.sort((a, b) => {
          if (a.total !== b.total) return a.total - b.total;
          return b.longestStreak - a.longestStreak;
        });

        // Assign ranks with tie-aware logic
        let rank = 0;
        let sameRankCount = 0;
        for (let i = 0; i < leaderboardData.length; i++) {
          if (i > 0 && 
              leaderboardData[i].total === leaderboardData[i - 1].total &&
              leaderboardData[i].longestStreak === leaderboardData[i - 1].longestStreak) {
            leaderboardData[i].rank = leaderboardData[i - 1].rank;
            sameRankCount++;
          } else {
            rank += 1 + sameRankCount;
            sameRankCount = 0;
            leaderboardData[i].rank = rank;
          }
        }

        results.push({
          group,
          userStats: {
            totalDrinks,
            longestStreak,
            beerCount,
            wineCount,
            shotCount,
          },
          leaderboard: leaderboardData,
        });
      }

      // Sort by challenge end date (most recent first)
      results.sort((a, b) => 
        new Date(b.group.challenge_end).getTime() - new Date(a.group.challenge_end).getTime()
      );

      return results;
    },
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('pastChallengesTitle')}</h2>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!pastChallenges || pastChallenges.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">{t('pastChallengesTitle')}</h2>
      {pastChallenges.map((challenge) => (
        <PastChallengeCard
          key={challenge.group.id}
          group={challenge.group}
          userStats={challenge.userStats}
          leaderboard={challenge.leaderboard}
          userNickname={profile?.user_id || ''}
        />
      ))}
    </div>
  );
}

// Helper function to calculate longest streak
function calculateLongestStreak(
  drinkLogs: { logged_at: string }[],
  group: { challenge_start: string; challenge_end: string }
): number {
  const start = new Date(group.challenge_start);
  start.setHours(0, 0, 0, 0);
  
  // Use challenge_end as the reference for completed challenges
  const end = new Date(group.challenge_end);
  end.setHours(0, 0, 0, 0);

  if (!drinkLogs || drinkLogs.length === 0) {
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  }

  const sortedLogs = [...drinkLogs].sort((a, b) => 
    new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime()
  );

  let longestStreak = 0;

  // Streak from challenge start to first drink
  const firstDrink = new Date(sortedLogs[0].logged_at);
  firstDrink.setHours(0, 0, 0, 0);
  longestStreak = Math.max(longestStreak, Math.floor((firstDrink.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));

  // Streaks between drinks
  for (let i = 0; i < sortedLogs.length - 1; i++) {
    const current = new Date(sortedLogs[i].logged_at);
    current.setHours(0, 0, 0, 0);
    const next = new Date(sortedLogs[i + 1].logged_at);
    next.setHours(0, 0, 0, 0);
    const gap = Math.floor((next.getTime() - current.getTime()) / (1000 * 60 * 60 * 24)) - 1;
    longestStreak = Math.max(longestStreak, gap);
  }

  // Streak from last drink to challenge end
  const lastDrink = new Date(sortedLogs[sortedLogs.length - 1].logged_at);
  lastDrink.setHours(0, 0, 0, 0);
  longestStreak = Math.max(longestStreak, Math.floor((end.getTime() - lastDrink.getTime()) / (1000 * 60 * 60 * 24)));

  return longestStreak;
}
